import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from '@reduxjs/toolkit';
import type { CustomerInformationResponse } from '@/types/customer';
import { api } from '@/store/api';
import { setSessionCustomerInfo } from '@/store/slices/callSlice';

/**
 * Track the latest customer fetch per contact so that stale in-flight requests
 * cannot overwrite results from newer requests.  When the agent relinks a
 * customer to a different workspace, two fetchCustomerForCall calls can
 * overlap — the older one finishing last would otherwise silently restore
 * the previous workspace's customer data.
 *
 * Each fetch gets a unique sequence number so that repeated fetches for the
 * **same** workspace are also protected.
 */
let fetchCustomerSequence = 0;
const latestCustomerFetchByContact = new Map<string, number>();

/** Fetch customer info by phone and store in Redux. Dispatched when call stream init provides callerPhoneNumber. */
export const fetchCustomerForCall = createAsyncThunk(
  'customer/fetchForCall',
  async (
    { workSpaceID, contactId }: { workSpaceID: string; contactId?: string },
    { dispatch, getState }
  ) => {
    const fetchKey = contactId ?? '__no_contact__';
    const mySeq = ++fetchCustomerSequence;
    latestCustomerFetchByContact.set(fetchKey, mySeq);

    const result = await dispatch(
      api.endpoints.getCustomerInformation.initiate(
        {
          workSpaceID,
        },
        { subscribe: false, forceRefetch: true }
      )
    ).unwrap();

    // Discard if a newer fetch for the same contact was started while we
    // were waiting (e.g. agent re-linked to a different workspace).
    if (latestCustomerFetchByContact.get(fetchKey) !== mySeq) {
      return { customer: result, shouldUpdateFocused: false };
    }

    // Only update the session if it still exists — the contact may have been
    // removed (e.g. agent finished after-call work) while the fetch was in
    // flight.  ensureSession would otherwise recreate a deleted session and
    // re-focus it, resurrecting stale customer data.
    if (
      contactId &&
      (getState() as { call?: { sessions?: Record<string, unknown> } }).call
        ?.sessions?.[contactId]
    ) {
      dispatch(setSessionCustomerInfo({ contactId, customer: result }));
    }
    const focusedContactId = (
      getState() as { call?: { focusedContactId?: string | null } }
    ).call?.focusedContactId;
    return {
      customer: result,
      shouldUpdateFocused: !contactId || focusedContactId === contactId,
    };
  }
);

interface CustomerState {
  currentCustomer: CustomerInformationResponse | null;
  /** Workspace used for the active call's customer lookup (from init or link-customer). */
  workSpaceID: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: CustomerState = {
  currentCustomer: null,
  workSpaceID: null,
  isLoading: false,
  error: null,
};

const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    setCustomerInfo: (
      state,
      action: PayloadAction<CustomerInformationResponse | null>
    ) => {
      state.currentCustomer = action.payload;
      state.error = null;
    },
    clearCustomerInfo: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCustomerForCall.pending, (state, action) => {
        state.workSpaceID = action.meta.arg.workSpaceID;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerForCall.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        if (action.payload.shouldUpdateFocused) {
          state.currentCustomer = action.payload.customer;
          state.workSpaceID = action.meta.arg.workSpaceID;
        }
      })
      .addCase(fetchCustomerForCall.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? 'Failed to load customer';
      });
  },
});

export const { setCustomerInfo, clearCustomerInfo } = customerSlice.actions;
export default customerSlice.reducer;
